import { Injectable, OnDestroy, inject } from '@angular/core';
import { fromEvent } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { AppStateChangeService } from '@services/app-state-change.service';
import { AuthService } from '@services/auth.service';
import { OnlineStateService } from '@services/online-state.service';
import { PlatformService } from '@services/platform.service';
import { SubscriptionsBag } from '@utils/subscriptions-bag';
import { WebSocketController } from '@services/web-socket.controller';

@Injectable({
  providedIn: 'root'
})
export class ChatPresenceActivityService implements OnDestroy {
  private readonly webSocketController = inject(WebSocketController);
  private readonly authService = inject(AuthService);
  private readonly onlineStateService = inject(OnlineStateService);
  private readonly appStateChangeService = inject(AppStateChangeService);
  private readonly platformService = inject(PlatformService);

  private readonly idleTimeoutMs: number = 60_000;
  private readonly activeHeartbeatMs: number = 50_000;
  private readonly activityThrottleMs: number = 5_000;
  private readonly activityEventNames: string[] = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

  private readonly sb: SubscriptionsBag = new SubscriptionsBag();
  private idleTimerId: number | null = null;
  private heartbeatTimerId: number | null = null;
  private isStarted: boolean = false;
  private isPresenceActive: boolean = false;
  private lastInteractionAt: number = Date.now();
  private lastActivityEventAt: number = 0;

  public get isPresenceActiveNow(): boolean {
    return this.isPresenceActive;
  }

  public start(): void {
    if (this.isStarted) return;

    this.isStarted = true;
    this.init();
  }

  public ngOnDestroy(): void {
    this.clearIdleTimer();
    this.clearHeartbeat();
    this.sb.unsubscribeAll();
  }

  private init(): void {
    this.subscribeToState();
    this.subscribeToDomActivity();
    this.evaluateAndSyncPresence(false);
  }

  private subscribeToState(): void {
    this.sb.sub = this.authService.parsedUser$.pipe(
      distinctUntilChanged(),
    ).subscribe((parsedUser): void => {
      if (!parsedUser) {
        this.forceInactive();
        return;
      }

      this.registerInteraction();
    });

    this.sb.sub = this.onlineStateService.isOffline$.pipe(
      distinctUntilChanged(),
    ).subscribe((isOffline: boolean): void => {
      if (isOffline) {
        this.forceInactive();
        return;
      }

      this.evaluateAndSyncPresence(false);
    });

    this.sb.sub = this.webSocketController.isSocketConnectSubject.pipe(
      distinctUntilChanged(),
    ).subscribe((isConnected: boolean): void => {
      if (!isConnected) {
        this.forceInactive();
        return;
      }

      this.evaluateAndSyncPresence(false);
    });

    this.sb.sub = this.webSocketController.isSocketInitedSubject.pipe(
      distinctUntilChanged(),
    ).subscribe((isInited: boolean): void => {
      if (!isInited) {
        this.forceInactive();
        return;
      }

      this.evaluateAndSyncPresence(false);
    });

    this.sb.sub = this.appStateChangeService.appStateChange$.pipe(
      distinctUntilChanged(),
    ).subscribe((isAppActive: boolean): void => {
      if (!isAppActive) {
        this.forceInactive();
        return;
      }

      this.registerInteraction();
    });
  }

  private subscribeToDomActivity(): void {
    if (this.platformService.isSSR || typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    this.sb.sub = fromEvent(document, 'visibilitychange').subscribe((): void => {
      if (document.visibilityState === 'visible') {
        this.registerInteraction();
        return;
      }

      this.evaluateAndSyncPresence(false);
    });

    this.sb.sub = fromEvent(window, 'focus').subscribe((): void => {
      this.registerInteraction();
    });

    this.sb.sub = fromEvent(window, 'blur').subscribe((): void => {
      this.evaluateAndSyncPresence(false);
    });

    this.activityEventNames.forEach((eventName: string): void => {
      this.sb.sub = fromEvent(document, eventName).subscribe((): void => {
        this.onUserActivity();
      });
    });
  }

  private onUserActivity(): void {
    const now: number = Date.now();
    if ((now - this.lastActivityEventAt) < this.activityThrottleMs) {
      return;
    }

    this.lastActivityEventAt = now;
    this.registerInteraction(now);
  }

  private registerInteraction(now: number = Date.now()): void {
    this.lastInteractionAt = now;
    this.evaluateAndSyncPresence(false);
  }

  private evaluateAndSyncPresence(forceEmit: boolean): void {
    const shouldBeActive: boolean = this.shouldReportActivePresence();

    if (shouldBeActive) {
      this.scheduleIdleTimer();
      this.ensureHeartbeat();
    } else {
      this.clearIdleTimer();
      this.clearHeartbeat();
    }

    if (!forceEmit && this.isPresenceActive === shouldBeActive) {
      return;
    }

    this.isPresenceActive = shouldBeActive;
    this.emitPresence(shouldBeActive);
  }

  private shouldReportActivePresence(): boolean {
    if (!this.authService.isAuthenticated) return false;
    if (this.onlineStateService.isOffline) return false;
    if (!this.webSocketController.socketConnected) return false;
    if (!this.webSocketController.isSocketInitedSubject.value) return false;
    if (!this.appStateChangeService.appIsActive) return false;
    if (!this.isWindowVisibleAndFocused()) return false;

    return !this.isIdleTimeoutReached();
  }

  private isIdleTimeoutReached(now: number = Date.now()): boolean {
    if (!this.lastInteractionAt) return true;
    return (now - this.lastInteractionAt) >= this.idleTimeoutMs;
  }

  private isWindowVisibleAndFocused(): boolean {
    if (this.platformService.isSSR || typeof document === 'undefined') {
      return true;
    }

    if (document.visibilityState !== 'visible') {
      return false;
    }

    if (typeof document.hasFocus !== 'function') {
      return true;
    }

    return document.hasFocus();
  }

  private scheduleIdleTimer(): void {
    if (typeof window === 'undefined') return;

    this.clearIdleTimer();

    const now: number = Date.now();
    const elapsedMs: number = now - this.lastInteractionAt;
    const timeoutMs: number = Math.max(0, this.idleTimeoutMs - elapsedMs);

    this.idleTimerId = window.setTimeout((): void => {
      this.evaluateAndSyncPresence(false);
    }, timeoutMs);
  }

  private clearIdleTimer(): void {
    if (this.idleTimerId === null || typeof window === 'undefined') return;

    window.clearTimeout(this.idleTimerId);
    this.idleTimerId = null;
  }

  private ensureHeartbeat(): void {
    if (typeof window === 'undefined' || this.heartbeatTimerId !== null) return;

    this.heartbeatTimerId = window.setInterval((): void => {
      if (!this.isPresenceActive) return;
      if (!this.shouldReportActivePresence()) {
        this.evaluateAndSyncPresence(false);
        return;
      }

      this.emitPresence(true);
    }, this.activeHeartbeatMs);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimerId === null || typeof window === 'undefined') return;

    window.clearInterval(this.heartbeatTimerId);
    this.heartbeatTimerId = null;
  }

  private forceInactive(): void {
    this.clearIdleTimer();
    this.clearHeartbeat();

    if (!this.isPresenceActive) {
      return;
    }

    this.isPresenceActive = false;
    this.emitPresence(false);
  }

  private emitPresence(isActive: boolean): void {
    if (!this.authService.isAuthenticated) return;
    if (!this.webSocketController.socketConnected) return;
    if (!this.webSocketController.isSocketInitedSubject.value) return;

    this.webSocketController.emitChatPresenceActivity(isActive);
  }
}
